import { Component, OnInit, ViewChild, ElementRef, AfterViewInit } from '@angular/core';
import MetisMenu from 'metismenujs';
import { Router, NavigationEnd } from '@angular/router';

import { EventService } from '../../../core/services/event.service';


import { MENU } from './menu';
import { MENU as MENU_EXTERNAL } from './menu-external';
import { MENU as MENU_INTERNAL } from './menu-internal';
import { MENU as MENU_TECH } from './menu-tech';
import { MENU as MENU_COLLECTOR } from './menu-collector';
import { MenuItem } from './menu.model';
import { LoginService } from 'src/app/core/services/login.service';
import { UserType } from 'src/app/core/models/userType.models';
import { FusionConnectService } from 'src/app/core/services/fusion-connect.service';

@Component({
  selector: 'app-sidebar',
  templateUrl: './sidebar.component.html',
  styleUrls: ['./sidebar.component.scss']
})
export class SidebarComponent implements OnInit, AfterViewInit {
  
  menu: any;

  menuItems = [];
  private hasLiveStatsForCurrentRep = true;

  @ViewChild('sideMenu') sideMenu: ElementRef;

  constructor(
    private eventService: EventService,
    private router: Router,
    private loginService: LoginService,
    private fusionConnectService: FusionConnectService
  ) {
    router.events.forEach((event) => {
      if (event instanceof NavigationEnd) {
        this._activateMenuDropdown();
      }
    });
  }

  async ngOnInit(): Promise<void> {
    await this.initialize();
  }

  ngAfterViewInit() {
    this.menu = new MetisMenu(this.sideMenu.nativeElement);

    this._activateMenuDropdown();
  }

  /**
   * remove active and mm-active class
   */
  _removeAllClass(className) {
    const els = document.getElementsByClassName(className);
    while (els[0]) {
      els[0].classList.remove(className);
    }
  }

  /**
   * Activate the parent dropdown
   */
  _activateMenuDropdown() {
    this._removeAllClass('mm-active');
    this._removeAllClass('mm-show');
    const links = document.getElementsByClassName('side-nav-link-ref');
    let menuItemEl = null;

    const paths = [];
    // tslint:disable-next-line: prefer-for-of
    for (let i = 0; i < links.length; i++) {
      // tslint:disable-next-line: no-string-literal
      paths.push(links[i]['pathname']);
    }
    const itemIndex = paths.indexOf(window.location.pathname);
    if (itemIndex === -1) {
      const strIndex = window.location.pathname.lastIndexOf('/');
      const item = window.location.pathname.substr(0, strIndex).toString();
      menuItemEl = links[paths.indexOf(item)];
    } else {
      menuItemEl = links[itemIndex];
    }

    if (menuItemEl) {
      menuItemEl.classList.add('active');
      const parentEl = menuItemEl.parentElement;

      if (parentEl) {
        parentEl.classList.add('mm-active');

        const parent2El = parentEl.parentElement.closest('ul');
        if (parent2El && parent2El.id !== 'side-menu') {
          parent2El.classList.add('mm-show');
          const parent3El = parent2El.parentElement;

          if (parent3El && parent3El.id !== 'side-menu') {
            parent3El.classList.add('mm-active');
            const childAnchor = parent3El.querySelector('.has-arrow');
            const childDropdown = parent3El.querySelector('.has-dropdown');

            if (childAnchor) { childAnchor.classList.add('mm-active'); }
            if (childDropdown) { childDropdown.classList.add('mm-active'); }

            const parent4El = parent3El.parentElement;
            if (parent4El && parent4El.id !== 'side-menu') {
              parent4El.classList.add('mm-show');
              const parent5El = parent4El.parentElement;
              if (parent5El && parent5El.id !== 'side-menu') {
                parent5El.classList.add('mm-active');
                const childanchor = parent5El.querySelector('.is-parent');
                if (childanchor && parent5El.id !== 'side-menu') { childanchor.classList.add('mm-active'); }
              }
            }
          }
        }
      }
    }
  }

  /**
   * Initialize
   */
  private filterMenuForCurrentUser(items: MenuItem[]): MenuItem[] {
    const restrictedSalesUser = this.loginService.userType === UserType.EXTERNAL || this.loginService.userType === UserType.CUSTOMER;
    if (!restrictedSalesUser) {
      return items;
    }

    const blockedLinks = new Set(['/pending-collections', '/users', '/salesreps']);
    if (!this.hasLiveStatsForCurrentRep) {
      blockedLinks.add('/fusion-connect');
      blockedLinks.add('/fusion-connect-stats');
    }

    return (items || []).filter((item) => !item.link || !blockedLinks.has(item.link));
  }

  private async detectExternalLiveStatsAvailability(): Promise<boolean> {
    if (this.loginService.userType !== UserType.EXTERNAL) {
      return true;
    }

    const today = new Date();
    const start = new Date(today.getFullYear(), today.getMonth(), 1);

    try {
      const result = await this.fusionConnectService.fetchDailyStats({
        from: start.toISOString().slice(0, 10),
        to: today.toISOString().slice(0, 10),
        page: 0,
        pageSize: 1,
      });

      return Array.isArray(result?.content) && result.content.length > 0;
    } catch {
      return false;
    }
  }

  async initialize(): Promise<void> {
    let items: MenuItem[] = [];

    this.hasLiveStatsForCurrentRep = await this.detectExternalLiveStatsAvailability();

    switch(this.loginService.userType) {
      case UserType.EXTERNAL:
        items = MENU_EXTERNAL;
        break;
      case UserType.COLLECTOR:
        items = MENU_COLLECTOR;
        break;
      case UserType.INTERNAL:
        items = MENU_INTERNAL;
        break;
      case UserType.TECH:
        items = MENU_TECH;
        break;
      case UserType.ROOT:
        items = MENU;
        break;
      default:
        items = MENU_EXTERNAL;
        break;
    }

    this.menuItems = this.filterMenuForCurrentUser(items);
  }

  /**
   * Returns true or false if given menu item has child or not
   * @param item menuItem
   */
  hasItems(item: MenuItem) {
    return item.subItems !== undefined ? item.subItems.length > 0 : false;
  }

  /**
   * Change the layout onclick
   * @param layout Change the layout
   */
  changeLayout(layout: string) {
    this.eventService.broadcast('changeLayout', layout);
  }

  /**
   * Light sidebar
   */
  lightSidebar() {
    document.body.setAttribute('data-sidebar', 'light');
    document.body.setAttribute('data-topbar', 'dark');
    document.body.removeAttribute('data-sidebar-size');
    document.body.removeAttribute('data-layout-size');
    document.body.removeAttribute('data-keep-enlarged');
    document.body.classList.remove('vertical-collpsed');
  }

  /**
   * Compact sidebar
   */
  compactSidebar() {
    document.body.setAttribute('data-sidebar-size', 'small');
    document.body.setAttribute('data-sidebar', 'dark');
    document.body.removeAttribute('data-topbar');
    document.body.removeAttribute('data-layout-size');
    document.body.removeAttribute('data-keep-enlarged');
    document.body.classList.remove('sidebar-enable');
    document.body.classList.remove('vertical-collpsed');
  }

  /**
   * Icon sidebar
   */
  iconSidebar() {
    document.body.classList.add('sidebar-enable');
    document.body.classList.add('vertical-collpsed');
    document.body.setAttribute('data-sidebar', 'dark');
    document.body.removeAttribute('data-layout-size');
    document.body.removeAttribute('data-keep-enlarged');
    document.body.removeAttribute('data-topbar');
  }

  /**
   * Boxed layout
   */
  boxedLayout() {
    document.body.setAttribute('data-keep-enlarged', 'true');
    document.body.setAttribute('data-layout-size', 'boxed');
    document.body.setAttribute('data-sidebar', 'dark');
    document.body.classList.add('vertical-collpsed');
    document.body.classList.remove('sidebar-enable');
    document.body.removeAttribute('data-topbar');
  }

  /**
   * Colored sidebar
   */
  coloredSidebar() {
    document.body.setAttribute('data-sidebar', 'colored');
    document.body.removeAttribute('data-sidebar-size');
    document.body.removeAttribute('data-layout-size');
    document.body.classList.remove('vertical-collpsed');
    document.body.removeAttribute('data-topbar');
  }

}
